import React, { Fragment, useMemo } from "react";

const formatList = (raw) => {
  if (!raw) return "";
  if (Array.isArray(raw)) {
    return raw.filter(Boolean).join(", ");
  }
  if (typeof raw === "string") {
    return raw.trim();
  }
  return "";
};

const formatValue = (value) => {
  if (value === undefined || value === null) return "";
  if (typeof value === "string" && value.trim() === "") return "";
  return value;
};

const formatMeasurement = (value, unit) => {
  if (value === undefined || value === null || value === "") return "";
  return unit ? `${value} ${unit}` : `${value}`;
};

const formatLocation = (location) => {
  if (!location) return "";
  const { city, state, country } = location;
  const parts = [city, state, country].filter(Boolean);
  return parts.join(", ");
};

const Section = ({ title, rows }) => {
  if (!rows.length) return null;
  return (
    <section className="mb-6 last:mb-0">
      <h3 className="text-sm font-semibold tracking-wide text-gray-500 uppercase mb-3">
        {title}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
        {rows.map(({ label, value }) => (
          <div key={label} className="min-w-0">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
              {label}
            </p>
            <p className="text-base text-gray-800 font-medium break-words">
              {value}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
};

const buildSections = (profile) => {
  if (!profile) return [];
  const basic = [
    { label: "Name", value: formatValue(profile.name) },
    { label: "Age", value: formatValue(profile.age) },
    { label: "Gender", value: formatValue(profile.gender) },
    { label: "Looking For", value: formatValue(profile.lookingFor) },
  ].filter((row) => Boolean(row.value));

  const identity = [
    { label: "Location", value: formatLocation(profile.location) },
    { label: "Languages", value: formatList(profile.languages) },
    { label: "Ethnicities", value: formatList(profile.ethnicities) },
    { label: "Nationalities", value: formatList(profile.nationalities) },
  ].filter((row) => Boolean(row.value));

  const lifestyle = [
    { label: "Exercise", value: formatValue(profile.exercise) },
    { label: "Drinking", value: formatValue(profile.drinking) },
    { label: "Smoking", value: formatValue(profile.smoking) },
    { label: "Pets", value: formatValue(profile.pets) },
    { label: "Height", value: formatMeasurement(profile.height, "cm") },
    { label: "Weight", value: formatMeasurement(profile.weight, "kg") },
    { label: "Orientation", value: formatValue(profile.sexualOrientation) },
    { label: "Star Sign", value: formatValue(profile.starSign) },
    { label: "Education", value: formatValue(profile.educationLevel) },
    { label: "Marital Status", value: formatValue(profile.maritalStatus) },
    { label: "Kids", value: formatValue(profile.kids) },
    { label: "Religion", value: formatValue(profile.religion) },
  ].filter((row) => Boolean(row.value));

  return [
    { title: "Basic Info", rows: basic },
    { title: "Identity & Background", rows: identity },
    { title: "Lifestyle & Personal Details", rows: lifestyle },
  ].filter((section) => section.rows.length);
};

const ProfileDetailBody = ({ profile }) => {
  const sections = useMemo(() => buildSections(profile), [profile]);
  const aboutCopy =
    profile?.bio || profile?.aboutMe || profile?.description || "";
  const interests = useMemo(() => {
    if (Array.isArray(profile?.interests) && profile.interests.length) {
      return profile.interests.filter(Boolean);
    }
    return [];
  }, [profile?.interests]);

  if (!profile) return null;

  return (
    <div className="space-y-6">
      <header className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-3xl font-bold text-gray-900">
            {profile.name}
            {profile.age ? <span>, {profile.age}</span> : null}
          </h2>
          {profile.gender && (
            <span className="px-3 py-1 rounded-full bg-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-700">
              {profile.gender}
            </span>
          )}
          {profile.lookingFor && (
            <span className="px-3 py-1 rounded-full bg-indigo-50 text-xs font-semibold uppercase tracking-wide text-indigo-700">
              Looking for: {profile.lookingFor}
            </span>
          )}
        </div>
        {profile.location && (
          <p className="text-gray-500 flex items-center gap-2">
            <span className="text-lg">📍</span>
            <span>{formatLocation(profile.location)}</span>
          </p>
        )}
      </header>

      {aboutCopy && (
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-2">
            About Me
          </h3>
          <p className="text-base leading-relaxed text-gray-700 whitespace-pre-line">
            {aboutCopy}
          </p>
        </div>
      )}

      {sections.map((section) => (
        <Fragment key={section.title}>
          <div className="h-px bg-gray-100" />
          <Section title={section.title} rows={section.rows} />
        </Fragment>
      ))}

      {interests.length > 0 && (
        <div>
          <div className="h-px bg-gray-100 mb-4" />
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">
            Interests
          </h3>
          <div className="flex flex-wrap gap-2">
            {interests.map((interest) => (
              <span
                key={interest}
                className="px-3 py-1 rounded-full bg-slate-100 text-sm font-medium text-gray-700"
              >
                {interest}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDetailBody;
